import { useCallback, useContext, useEffect, useMemo, useReducer, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Form } from "react-bootstrap";

import AddDefendantModal from "@/Components/DefendantPage/DefandantMain/modals/AddDefendantModal";
import ClientEditModal from "@/Components/ClientDashboard/Modal/ClientModal";

import usePageAttachmentTabs from "../Hooks/usePageAttachmentTabs";
import Footer from "../Components/common/footer";
import Sidebar from "../Components/Sidebars/main";
import NavBar from "../Components/Navbars/main";
import {
  formatCalendarDate,
  addClickRecord,
  currencyFormat,
  fetchPageObject,
  fetchShakespeareStatus,
  formatDateForPanelDisplay,
  formatWithCommas,
  getCaseId,
  getClientId,
} from "../Utils/helper";
import ActionBarComponent from "../Components/common/ActionBarComponent";
import PageDocsTab, { createDocsTab } from "../Components/DocPage/new-tab/page-docs-tab";
import SmartResponsiveTabsContainer from "../Components/DefendantPage/DefandantMain/components/ResponsiveTabDefendants";
import addIconGold from "../assets/Add-icon-gold.svg";
import AddOtherPartiesModal from "../Components/OtherPartiesDashboard/addOtherPartiesModal";
import NotesSectionDashboard from "../Components/NotesSectionDashboard/main";
import useGetAllParties from "../Components/OtherPartiesDashboard/hooks/useGetAllParties";
import TreatmentNotesSection from "../Components/TreatmentPage/NotesTreatmentSection";
import CounselMain from "../Components/DefendantPage/Counsel/CounselMain";
import { ClientDataContext } from "../Components/ClientDashboard/shared/DataContext";
import { api_without_cancellation } from "../api/api";
import { deleteInsurance } from "../Redux/insurance/insuranceSlice";
import DocumentRow from "../Components/DocumentRow/DocumentRow";
import InsuranceModal from "../Components/Insurance/InsuranceModal";
import GenrateDocument from "../Components/GenrateDocument/GenrateDocument";
import ConfirmDeletePopup from "../Components/DefendantPage/DefandantMain/ConfirmDeletePopup";
import PanelActionBarComponent from "../Components/common/PanelActionBarComponent";
import ContactPanelForTreatment from "../Components/TreatmentPage/components/ContactPanelForTreatment";
import InformationPanel from "../Components/common/InformationPanel";
import PlaceHolderifNothingAdded from "../Components/components-new-final/placeholder-nothing-added";
import "../Components/common/RecordPanel.css";

function OtherPartiesPage() {
  const [sideBarCollapsed, setSideBarCollapsed] = useState(null);
  const dispatch = useDispatch();
  const caseId = getCaseId();
  const clientId = getClientId();

  const tokenBearer = useSelector((state) => state.auth?.token);
  const origin = window.location.origin;

  const [pageObject, setPageObject] = useState(null);

  useEffect(() => {
    if (caseId && !pageObject) {
      fetchPageObject(caseId, 9).then((data) => setPageObject(data));
    }
  }, [caseId, pageObject]);

  useEffect(() => {
    fetchShakespeareStatus(getCaseId(), 9, dispatch);
  }, [dispatch]);

  useEffect(() => {
    const preferences = localStorage.getItem("userPreferences");
    const preferencesObj = preferences ? JSON.parse(preferences) : null;
    setSideBarCollapsed(preferencesObj?.SidebarView);
  }, []);

  const [currentPage, setCurrentPage] = useState("all");
  const [isDummy, setIsDummy] = useState(false);
  const [show, setShow] = useState(false);

  const handlePageChange = useCallback((pageType) => {
    setCurrentPage(pageType);
  }, []);

  const docsTabs = useMemo(
    () => [createDocsTab(() => handlePageChange("docs"))],
    [handlePageChange]
  );
  const attachmentTabs = usePageAttachmentTabs(9, docsTabs);

  const handleCloseAddModal = () => setShow(false);

  const { parties, client, defendant, refetch } = useGetAllParties();

  const fetchDummyStatus = async () => {
    try {
      const response = await api_without_cancellation.get(
        `/api/general/panel_page_block/?case_id=${getCaseId()}&page_id=${9}`,
        {
          headers: {
            "Content-Type": "application/json",
          },
        }
      );
      setIsDummy(response?.data?.status);
    } catch (error) {
      console.error("Error fetching dummy status:", error);
    }
  };

  useEffect(() => {
    fetchDummyStatus();
  }, []);

  const mainTabs = useMemo(() => {
    const tabs = [
      {
        id: "all",
        label: "All",
        className: "tab-trapezium-cycles-corners",
        background: "var(--primary-5)",
        activeColor: "white",
        onClick: () => handlePageChange("all"),
        leftHand: false,
      },
      {
        id: "client",
        label: "Client",
        className: "tab-trapezium-cycles-corners",
        background: "var(--primary-5)",
        activeColor: "white",
        onClick: () => handlePageChange("client"),
        leftHand: true,
        span: (
          <span className="d-flex align-items-center justify-content-center" style={{ gap: "5px" }}>
            <i className={"ic ic-15 ic-client"} />
          </span>
        ),
      },
      {
        id: "defendant",
        label: "Defendant",
        className: "tab-trapezium-cycles-corners",
        background: "var(--primary-5)",
        activeColor: "white",
        onClick: () => handlePageChange("defendant"),
        leftHand: true,
        span: (
          <span className="d-flex align-items-center justify-content-center" style={{ gap: "5px" }}>
            <i className={"ic ic-15 ic-defendants"} />
          </span>
        ),
      },
    ].filter((tab) => {
      if (tab.id === "client") return client?.length > 0;
      if (tab.id === "defendant") return defendant?.length > 0;
      return true;
    });

    parties?.forEach((p) => {
      const midInitial = p?.party_middle_name
        ? `${p.party_middle_name.charAt(0).toUpperCase()}.`
        : "";
      const fullName =
        `${p?.party_first_name ? p?.party_first_name + "," : ""} ${p?.party_last_name ?? ""} ${midInitial}`.trim();
      const typeName = p?.party_type_ID?.name ?? "";
      const label = [typeName, fullName].filter(Boolean).join(" ") || "Unnamed Party";

      tabs.push({
        id: String(p.id),
        label,
        className: "tab-trapezium-cycles-corners",
        background: "var(--primary-5)",
        activeColor: "white",
        onClick: () => handlePageChange(String(p.id)),
        leftHand: true,
        span: (
          <span className="d-flex align-items-center justify-content-center" style={{ gap: "5px" }}>
            <i className={"ic ic-15 ic-parties"} />
          </span>
        ),
      });
    });

    return tabs;
  }, [parties, client, defendant, handlePageChange]);

  const buttonTabs = useMemo(
    () => [
      {
        id: "defendant-add",
        label: "Party",
        className: "tab-trapezium-cycles-corners",
        background: "var(--primary-60)",
        left: "0px",
        onClick: () => setShow(true),
        leftHand: true,
        span: (
          <span className="fw-bold ic-15 d-flex align-items-center">
            <img className="d-flex align-items-center justify-content-center" src={addIconGold} />
          </span>
        ),
      },
    ],
    []
  );

  const visibleParties = useMemo(() => {
    if (!parties) return [];
    if (currentPage === "all") return parties;
    if (currentPage === "client" || currentPage === "defendant") return [];
    return parties.filter((p) => String(p.id) === currentPage);
  }, [parties, currentPage]);
  useEffect(() => {
    if (currentPage === "all" || currentPage === "docs") return;
    const stillExists = mainTabs.some((tab) => tab.id === currentPage);
    if (!stillExists) setCurrentPage("all");
  }, [mainTabs, currentPage]);
  const [showEditPartyModal, setShowEditPartyModal] = useState(false);
  const [editPartyData, setEditPartyData] = useState(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [forDeletePartyId, setForDeletePartyId] = useState(null);
  const selectedEditableTabPanel = useRef("");

  // Counsel modal state
  const [forDeleteCounselId, setForDeleteCounselId] = useState(null);
  const [showDeleteCounselConfirm, setShowDeleteCounselConfirm] = useState(false);

  // Insurance modal state
  const [editInsuranceModal, setEditInsuranceModal] = useState({});
  const [selectedInsuranceParty, setSelectedInsuranceParty] = useState(null);
  const [showDeleteInsuranceModal, setShowDeleteInsuranceModal] = useState(false);
  const [insuranceModalShow, setInsuranceModalShow] = useState(false);
  const [selectedInsuranceDeletionId, setSelectedInsuranceDeletionId] = useState(null);
  const [activeInsuranceTab, setActiveInsuranceTab] = useState("insurance");
  const { isPanelChecklistUpdated, setIsPanelChecklistUpdated } = useContext(ClientDataContext);

  // Generate document state
  const [showDocument, setShowDocument] = useState(false);
  const [instanceIdForGenrateDoc, setInstanceIdForGenragteDoc] = useState(null);
  const [dropdownName, setDropdownName] = useState("");

  const [reducerValue, setReducer] = useReducer((x) => x + 1, 0);
  const { modalData } = useSelector((state) => state.insurances);

  // Medical bill data — required by InsuranceModal's billData prop
  const [medicalBillData, setMedicalBillData] = useState(null);

  const fetchMedicalBillData = useCallback(async () => {
    if (!caseId || !clientId) return;
    try {
      const res = await api_without_cancellation.get(
        `/api/settlement-page/lien-information/?case_id=${caseId}&client_id=${clientId}`
      );
      if (res.status === 200) setMedicalBillData(res.data?.data);
    } catch (err) {
      console.error("Failed to fetch medical bill data:", err);
    }
  }, [caseId, clientId, origin, tokenBearer]);

  useEffect(() => {
    fetchMedicalBillData();
  }, [fetchMedicalBillData]);

  // Refetch lien data when an insurance modal closes — lien edits there change totals here
  useEffect(() => {
    if (!insuranceModalShow) fetchMedicalBillData();
  }, [insuranceModalShow, fetchMedicalBillData]);

  const handleDeleteCounselSubmission = async () => {
    try {
      const res = await api_without_cancellation.delete(
        `/api/defendants/delete_opposing_counsel/${forDeleteCounselId}/`
      );
      if (res.status === 204) {
        setShowDeleteCounselConfirm(false);
        refetch();
      }
    } catch (err) {
      console.error("Error deleting counsel:", err);
    }
  };

  const handleDeletePartySubmission = async () => {
    try {
      const res = await api_without_cancellation.delete(
        `/api/otherparties/delete_party/${forDeletePartyId}/`
      );
      if (res.status === 204) {
        hideDeleteConfirmPopup();
        refetch();
        setIsPanelChecklistUpdated(!isPanelChecklistUpdated);
        setReducer();
      }
    } catch (err) {
      console.error("Error deleting party:", err);
    } finally {
      hideDeleteConfirmPopup();
    }
  };

  const handleCloseFunction = () => {
    setShowEditPartyModal(false);
    setEditPartyData(null);
  };

  const handleGenrateDocument = (instanceId, dropDownName) => {
    setDropdownName(dropDownName);
    setInstanceIdForGenragteDoc(instanceId);
    setShowDocument(true);
  };

  const showDeleteConfirmPopup = () => {
    setShowDeleteConfirm(true);
    setShowEditPartyModal(false);
    setEditPartyData(null);
  };

  const hideDeleteConfirmPopup = () => setShowDeleteConfirm(false);

  // Open edit modal for a given party + which tab to land on
  const openEditForParty = (party, tab) => {
    setEditPartyData(party);
    setForDeletePartyId(party?.id);
    selectedEditableTabPanel.current = tab;
    setShowEditPartyModal(true);
  };

  const handleEditModalShow = (tab) => {
    setActiveInsuranceTab(tab);
    setInsuranceModalShow(true);
  };

  const extractDataForEditModal = (data, party) => {
    setEditInsuranceModal(data);
    setSelectedInsuranceParty({
      id: party.id,
      type: "OtherParty",
      name:
        [party.party_first_name || party.first_name, party.party_last_name || party.last_name]
          .filter(Boolean)
          .join(" ") ||
        party.entity_name ||
        "Other Party",
    });
  };

  const handleDelete = async () => {
    await dispatch(deleteInsurance({ insurance_id: selectedInsuranceDeletionId }));
    setShowDeleteInsuranceModal(false);
    setSelectedInsuranceDeletionId(null);
    refetch();
    fetchMedicalBillData();
  };

  const deleteInsuranceHandler = (id) => {
    setInsuranceModalShow(false);
    setShowDeleteInsuranceModal(true);
    setSelectedInsuranceDeletionId(id);
  };

  const LienButtonsConfig = [
    {
      id: "generate-document-button",
      iconClassName: "ic ic-15 ic-generate-document",
      buttonText: "Letter",
      className: "p-l-6 p-r-5",
      style: { height: "25px" },
      onClick: () => {},
    },
  ];

  const InsuranceButtons = [
    {
      id: "generate-document-button",
      iconClassName: "ic ic-15 ic-generate-document",
      buttonText: "Letter",
      className: "p-l-6 p-r-5",
      style: { height: "25px" },
      onClick: () => {},
    },
  ];

  const EmploymentButtonsConfig2 = [
    {
      id: "generate-document-button",
      iconClassName: "ic ic-19 m-l-5",
      imgClassName: "ic ic-19 m-r-5",
      buttonText: "",
      className: "p-l-6 p-r-5",
      style: { height: "25px" },
      onHover: () => {},
      onMouseLeave: () => {},
      onClick: () => {},
    },
  ];

  const [height, setHeight] = useState(null);
  const insuranceHeightRef = useRef(null);
  const heightRef = useRef(null);

  useEffect(() => {
    if (!heightRef.current) return;
    setHeight(heightRef.current.clientHeight);
    const ro = new ResizeObserver((entries) => {
      for (const entry of entries) setHeight(entry.contentRect.height);
    });
    ro.observe(heightRef.current);
    return () => ro.disconnect();
  }, []);

  function roundToInt(value) {
    const number = parseFloat(value) || 0; // Convert value to a float
    return Math.round(number); // Proper rounding without skewing
  }

  const EmploymentButtonsConfig = [
    {
      id: "verified-button",
      iconClassName: "ic ic-15 ic-generate-document",
      buttonText: "Letter",
      className: "p-l-6 p-r-5",
      style: { height: "25px" },
      onClick: () => {},
    },
  ];
  const [editDefendantPartyType, setEditDefendantPartyType] = useState(false);
  const [editClientPartyType, setEditClientPartyType] = useState(false);
  const [selectedData, setSelectedData] = useState(null);
  const [PageEntity, setPageEntity] = useState("");
  const DefendantButtonsConfig = [
    {
      id: "generate-document-button",
      iconClassName: "ic ic-15 ic-generate-document",
      buttonText: "Letter",
      className: "p-l-6 p-r-5",
      style: { height: "25px" },
      onClick: (id, name) => {
        handleGenrateDocument(id, name);
        setPageEntity("Client");
      },
    },
  ];

  const DefendantButtonsConfig2 = [
    {
      id: "generate-document-button",
      iconClassName: "ic ic-15 ic-generate-document",
      buttonText: "Letter",
      className: "p-l-6 p-r-5",
      style: { height: "25px" },
      onClick: (id, name) => {
        handleGenrateDocument(id, name);
        setPageEntity("Defendants");
      },
    },
  ];

  const DefendantButtonsConfig3 = [
    {
      id: "generate-document-button",
      iconClassName: "ic ic-15 ic-generate-document",
      buttonText: "Letter",
      className: "p-l-6 p-r-5",
      style: { height: "25px" },
      onClick: (id, name) => {
        handleGenrateDocument(id, name);
        setPageEntity("Parties");
      },
    },
  ];
  const allParties = [...parties, ...client, ...defendant];
  return (
    <>
      <div
        className={`page-wrapper settle-page-wrapper ${sideBarCollapsed ? "force-collapsed" : ""}`}
      >
        <Sidebar />
        <div className="page-container overflow-hidden">
          <NavBar flaggedPageName={"9"} />
          <ActionBarComponent
            src={pageObject?.page_icon}
            page_name={pageObject?.name}
            pageObj={pageObject}
            isChecklist={pageObject?.PageChklst || false}
          />

          <div className="TreatmentPage-main-container invisible-scrollbar">
            <SmartResponsiveTabsContainer
              isEmpty={isDummy || allParties?.length === 0}
              HandlePage={currentPage}
              tabsData={mainTabs}
              viewTab={[]}
              addTab={buttonTabs}
              photoTab={attachmentTabs}
              photoTabControlled={true}
              handlePageChange={handlePageChange}
            />

            {currentPage === "docs" && (
              <PageDocsTab
                key={`${clientId}-${caseId}`}
                pageId={9}
                pageName={pageObject?.name}
                pageIcon={pageObject?.page_icon}
              />
            )}

            {(currentPage === "all" || currentPage === "client") &&
              client?.map((cl) => {
                const lastName = cl?.last_name || "";
                const firstName = cl?.first_name || "";
                const label =
                  lastName && firstName
                    ? `${lastName}, ${firstName}`
                    : lastName
                      ? `${lastName}`
                      : `${firstName}`;
                return (
                  <>
                    <div className="report record-panel" key={cl?.id}>
                      <PanelActionBarComponent
                        compact
                        id={cl?.id}
                        type={cl?.party_type?.name}
                        name={label}
                        object={cl}
                        page_id={""}
                        hasIcon={true}
                        iconClassName="client"
                        // isFirst={true}
                      />

                      <div
                        className="flex-row d-flex record-panel-body flex-wrap"
                        style={{ overflow: "hidden", gap: "5px" }}
                      >
                        <div ref={heightRef} className="leins-container-three m-b-5">
                          <div className="min-w-260px-client">
                            <ContactPanelForTreatment
                              id={cl?.id}
                              panel_name={"Client Address 1"}
                              className="m-b-0"
                              pageName={"client"}
                              name={`${cl?.first_name} ${cl?.last_name}`}
                              dynamic_label={"Full Name"}
                              address1={cl?.contact_1?.address1}
                              address2={cl?.contact_1?.address2}
                              city={cl?.contact_1?.city}
                              state={cl?.contact_1?.state}
                              zip_code={cl?.contact_1?.zip}
                              phone_number={cl?.contact_1?.phone_number}
                              ext={cl?.contact_1?.phone_ext}
                              fax_number={cl?.contact_1?.fax}
                              email={cl?.contact_1?.email}
                              websiteURL={cl?.contact_1?.website}
                              buttonData={DefendantButtonsConfig}
                              // genrate_doc_address={"Client Address 1"}
                              genrate_doc_address={1}
                              templateDropdownId={1}
                              templatePageId={2}
                              onSelectObject={() => {
                                setEditClientPartyType(true);
                                setSelectedData(cl);
                              }}
                            />
                          </div>
                          <div className="min-w-260px-client">
                            <ContactPanelForTreatment
                              pageName={"client"}
                              id={cl?.id}
                              name={`${cl?.first_name} ${cl?.last_name}`}
                              panel_name={"Client Address 2"}
                              className="m-b-0"
                              phone_number={cl?.contact_2?.phone_number}
                              email={cl?.contact_2?.email}
                              address1={cl?.contact_2?.address1}
                              address2={cl?.contact_2?.address2}
                              city={cl?.contact_2?.city}
                              state={cl?.contact_2?.state}
                              zip_code={cl?.contact_2?.zip}
                              ext={cl?.contact_2?.phone_ext}
                              fax_number={cl?.contact_2?.fax}
                              websiteURL={cl?.contact_2?.email}
                              buttonData={DefendantButtonsConfig}
                              // genrate_doc_address={"Client Address 2"}
                              genrate_doc_address={14}
                              templateDropdownId={14}
                              templatePageId={2}
                              onSelectObject={() => {
                                setEditClientPartyType(true);
                                setSelectedData(cl);
                              }}
                            />
                          </div>
                          <div className="min-w-260px-client">
                            <InformationPanel
                              panel_name={"Information"}
                              className=""
                              panelClassName="m-b-0"
                              valueStyle={{
                                maxWidth: "70%",
                                display: "-webkit-box",
                                WebkitBoxOrient: "vertical",
                                WebkitLineClamp: 1,
                                overflow: "hidden",
                                // color: "var(--primary-25)",
                              }}
                              labelStyle={{
                                maxWidth: "50%",
                                display: "-webkit-box",
                                WebkitBoxOrient: "vertical",
                                WebkitLineClamp: 1,
                                overflow: "hidden",
                                // color: "var(--primary-25)",
                              }}
                              data={[
                                {
                                  label: "Gender",
                                  value: `${cl?.gender}`,
                                },
                                {
                                  label: "DOB",
                                  value: formatCalendarDate(cl?.birthday),
                                },
                                {},
                                {},
                                {},
                              ]}
                              onSelectReport={() => {
                                setEditClientPartyType(true);
                                setSelectedData(cl);
                              }}
                              defaultLength={5}
                              hasBtn={true}
                              buttonData={[
                                {
                                  id: "generate-document-button",

                                  buttonText: "",
                                  className: "p-l-6 p-r-5",
                                  style: { height: "25px" },
                                  onHover: () => {},
                                  onMouseLeave: () => {},
                                  onClick: () => {},
                                },
                              ]}
                            />
                          </div>
                        </div>
                        <div
                          className=""
                          style={{
                            flex: 1,
                            minHeight: "160px",
                            paddingRight: "0px",
                            scrollbarWidth: "none",
                            minWidth: "min(400px, 100%)",
                            maxHeight: `${height}px`,
                          }}
                        >
                          <p className="menu-bar-style columnsTitle text-center">Notes</p>
                          <TreatmentNotesSection
                            entity_type={"Client"}
                            record_id={cl?.id}
                            module={"Client"}
                            pagePanels={3}
                          />
                        </div>
                      </div>
                      <div className=" documents-wrapper">
                        <div className="col-12">
                          <DocumentRow
                            showSaveToPage={false}
                            pageName=""
                            clientProvider={getCaseId()}
                            page_id={2}
                          />
                        </div>
                      </div>
                    </div>
                  </>
                );
              })}

            {(currentPage === "all" || currentPage === "defendant") &&
              defendant?.map((def) => {
                let label = "";

                if (def?.defendantType === 1) {
                  // Private individual - "Last Name, First Name"
                  const lastName = def?.last_name || "";
                  const firstName = def?.first_name || "";
                  label =
                    lastName && firstName
                      ? `${lastName}, ${firstName}`
                      : lastName
                        ? `${lastName}`
                        : `${firstName}`;
                } else if (def?.defendantType === 2) {
                  // Commercial (2) or Public (3) entity - use entity name
                  label = `${def?.entity_name || "Entity"}`;
                } else {
                  label = `${def?.entity_name || "Entity"}`;
                }
                return (
                  <>
                    <div className="report record-panel" key={def?.id}>
                      {defendant[0]?.id === def?.id ? (
                        <PanelActionBarComponent
                          compact
                          id={def?.id}
                          name={label}
                          type={def?.party_type?.name}
                          object={def}
                          page_id={4}
                          zIndex={3}
                          hasIcon={true}
                          iconClassName="defendants"
                        />
                      ) : (
                        <PanelActionBarComponent
                          compact
                          id={def?.id}
                          type={label}
                          name={def?.party_type?.name}
                          object={def}
                          page_id={4}
                          hasIcon={true}
                          iconClassName="defendants"
                          // isFirst={true}
                        />
                      )}

                      <div
                        className="flex-row d-flex record-panel-body flex-wrap"
                        style={{ overflow: "hidden", gap: "5px" }}
                      >
                        <div ref={heightRef} className="leins-container-three m-b-5">
                          <div className="min-w-260px-client">
                            <ContactPanelForTreatment
                              id={def?.id}
                              panel_name={
                                def?.defendantType === 1
                                  ? "Contact Information"
                                  : def?.defendantType === 2
                                    ? "Company Information"
                                    : def?.defendantType === 3
                                      ? "Public Entity"
                                      : "Defendant"
                              }
                              className="m-b-0"
                              pageName={"defendant"}
                              name={
                                def?.defendantType === 1
                                  ? `${def?.first_name} ${def?.last_name}`
                                  : def?.entity_name
                              }
                              dynamic_label={def?.defendantType !== 1 ? "Entity Name" : ""}
                              address1={def?.home_contact?.address1}
                              address2={def?.home_contact?.address2}
                              city={def?.home_contact?.city}
                              state={def?.home_contact?.state}
                              zip_code={def?.home_contact?.zip}
                              phone_number={def?.home_contact?.phone_number}
                              ext={def?.home_contact?.phone_ext}
                              fax_number={def?.home_contact?.fax}
                              email={def?.home_contact?.email}
                              websiteURL={def?.home_contact?.website}
                              buttonData={DefendantButtonsConfig2}
                              // genrate_doc_address={"Defendant Address"}
                              genrate_doc_address={10}
                              templateDropdownId={10}
                              templatePageId={4}
                              onSelectObject={() => {
                                setEditDefendantPartyType(true);
                                setSelectedData(def);
                              }}
                            />
                          </div>
                          <div className="min-w-260px-client">
                            <ContactPanelForTreatment
                              pageName={"defendant"}
                              id={def?.id}
                              name={def?.defendant_employer}
                              panel_name={
                                def?.defendantType === 1
                                  ? "Employer"
                                  : def?.defendantType === 2
                                    ? "Agent For Service"
                                    : def?.defendantType === 3
                                      ? "Claims Of Department"
                                      : "Employer"
                              }
                              className="m-b-0"
                              phone_number={def?.work_contact?.phone_number}
                              email={def?.work_contact?.email}
                              address1={def?.work_contact?.address1}
                              address2={def?.work_contact?.address2}
                              city={def?.work_contact?.city}
                              state={def?.work_contact?.state}
                              zip_code={def?.work_contact?.zip}
                              ext={def?.work_contact?.phone_ext}
                              fax_number={def?.work_contact?.fax}
                              websiteURL={def?.work_contact?.email}
                              buttonData={EmploymentButtonsConfig}
                              onSelectObject={() => {
                                setEditDefendantPartyType(true);
                                setSelectedData(def);
                              }}
                              genrate_doc_address={15}
                              templateDropdownId={15}
                              templatePageId={4}
                            />
                          </div>
                          <div className="min-w-260px-client">
                            <InformationPanel
                              panel_name={"Information"}
                              className=""
                              panelClassName="m-b-0"
                              valueStyle={{
                                maxWidth: "70%",
                                display: "-webkit-box",
                                WebkitBoxOrient: "vertical",
                                WebkitLineClamp: 1,
                                overflow: "hidden",
                                // color: "var(--primary-25)",
                              }}
                              labelStyle={{
                                maxWidth: "50%",
                                display: "-webkit-box",
                                WebkitBoxOrient: "vertical",
                                WebkitLineClamp: 1,
                                overflow: "hidden",
                                // color: "var(--primary-25)",
                              }}
                              data={[
                                {
                                  label: "Type",

                                  value: (
                                    <span
                                      className="d-flex align-items-center justify-content-center"
                                      style={{ gap: "5px" }}
                                    >
                                      <i className={"ic ic-15 ic-defendants"} />
                                      {def?.defendantType_name}
                                    </span>
                                  ),
                                },
                                {
                                  label: "Liability",
                                  value: `${roundToInt(def?.liability_estimate)}% / ${roundToInt(def?.liability_percent)}% `,
                                },
                                {
                                  label: "Rep Letter",
                                  value: formatCalendarDate(def?.repr_letter_sent),
                                },
                                {
                                  label: "Contact",
                                  value: formatCalendarDate(def?.contact_date),
                                },

                                def?.defendantType === 1
                                  ? {
                                      label: "DOB",
                                      value: formatCalendarDate(def?.birthday),
                                    }
                                  : {},
                              ]}
                              onSelectReport={() => {
                                setEditDefendantPartyType(true);
                                setSelectedData(def);
                              }}
                              defaultLength={def?.defendantType !== 1 ? 5 : 5}
                              hasBtn={true}
                              buttonData={[
                                {
                                  id: "generate-document-button",

                                  buttonText: def?.defServedDate
                                    ? `Served: ${formatCalendarDate(def?.defServedDate)}`
                                    : "No Service Attempted",
                                  className: "p-l-6 p-r-5",
                                  style: { height: "25px" },
                                  onHover: () => {},
                                  onMouseLeave: () => {},
                                  onClick: () => {},
                                },
                              ]}
                            />
                          </div>
                        </div>
                        <div
                          className=""
                          style={{
                            flex: 1,
                            minHeight: "160px",
                            paddingRight: "0px",
                            scrollbarWidth: "none",
                            minWidth: "min(400px, 100%)",
                            maxHeight: `${height}px`,
                          }}
                        >
                          <p className="menu-bar-style columnsTitle text-center">Notes</p>
                          <TreatmentNotesSection
                            entity_type={"Defendants"}
                            record_id={def?.id}
                            module={"Defendants"}
                            pagePanels={3}
                          />
                        </div>
                      </div>
                      <div className=" documents-wrapper">
                        <div className="col-12">
                          <DocumentRow showSaveToPage={false} clientProvider={def} page_id={4} />
                        </div>
                      </div>
                    </div>
                  </>
                );
              })}

            {currentPage !== "docs" &&
              (currentPage === "all" ||
                (currentPage !== "client" && currentPage !== "defendant")) &&
              (isDummy ? (
                <PlaceHolderifNothingAdded
                  text1={"No Party on the Case"}
                  text2={"Page is Marked Complete"}
                  text3={"To Add a Party to the Case Click Here"}
                  pageIcon={pageObject?.page_icon}
                  completed
                  onClick={() => setShow(true)}
                />
              ) : visibleParties?.length === 0 ? (
                <>
                  <PlaceHolderifNothingAdded
                    text1={"No Party Added to Case"}
                    text2={"Click to Add"}
                    pageIcon={pageObject?.page_icon}
                    onClick={() => setShow(true)}
                  />
                </>
              ) : (
                visibleParties?.map((object) => (
                  <div key={object.id}>
                    <div style={{ position: "relative" }}>
                      <div className="report record-panel">
                        <PanelActionBarComponent
                          compact
                          id={object?.id}
                          type={`${object?.party_first_name || ""} ${object?.party_last_name}`}
                          name={object?.party_type_ID?.name}
                          object={object}
                          buttons={[]}
                          page_id={9}
                          hasIcon={true}
                          iconClassName="parties"
                        />

                        <div
                          className="flex-row d-flex record-panel-body flex-wrap"
                          style={{ overflow: "hidden", gap: "5px" }}
                        >
                          <div ref={heightRef} className="leins-container-three m-b-5">
                            <div className="min-w-260px-client">
                              <ContactPanelForTreatment
                                id={object?.id}
                                panel_name={"Contact"}
                                className="m-b-0"
                                pageName={"OtherParty"}
                                name={`${object?.party_first_name ?? ""} ${object?.party_last_name ?? ""}`}
                                dynamic_label={"Full name"}
                                address1={object?.party_home_contact?.address1}
                                address2={object?.party_home_contact?.address2}
                                city={object?.party_home_contact?.city}
                                state={object?.party_home_contact?.state}
                                zip_code={object?.party_home_contact?.zip}
                                phone_number={object?.party_home_contact?.phone_number}
                                ext={object?.party_home_contact?.phone_ext}
                                fax_number={object?.party_home_contact?.fax}
                                email={object?.party_home_contact?.email}
                                websiteURL={object?.party_home_contact?.website}
                                buttonData={DefendantButtonsConfig3}
                                // genrate_doc_address={"Party Address"}
                                genrate_doc_address={0}
                                templateDropdownId={0}
                                templatePageId={9}
                                onSelectObject={() => openEditForParty(object, "contact")}
                              />
                            </div>
                            <div className="min-w-260px-client">
                              <ContactPanelForTreatment
                                pageName={"OtherParty"}
                                id={object?.id}
                                name={object?.party_employer}
                                panel_name={"Employer"}
                                className="m-b-0"
                                phone_number={object?.party_work_contact?.phone_number}
                                email={object?.party_work_contact?.email}
                                address1={object?.party_work_contact?.address1}
                                address2={object?.party_work_contact?.address2}
                                city={object?.party_work_contact?.city}
                                state={object?.party_work_contact?.state}
                                zip_code={object?.party_work_contact?.zip}
                                ext={object?.party_work_contact?.phone_ext}
                                fax_number={object?.party_work_contact?.fax}
                                websiteURL={object?.party_work_contact?.website}
                                buttonData={DefendantButtonsConfig3}
                                genrate_doc_address={0}
                                templateDropdownId={0}
                                templatePageId={9}
                                onSelectObject={() => openEditForParty(object, "employer")}
                              />
                            </div>
                            <div className="min-w-260px-client">
                              <InformationPanel
                                panel_name={"Information"}
                                className=""
                                panelClassName="m-b-0"
                                valueStyle={{
                                  maxWidth: "70%",
                                  display: "-webkit-box",
                                  WebkitBoxOrient: "vertical",
                                  WebkitLineClamp: 1,
                                  overflow: "hidden",
                                }}
                                labelStyle={{
                                  maxWidth: "50%",
                                  display: "-webkit-box",
                                  WebkitBoxOrient: "vertical",
                                  WebkitLineClamp: 1,
                                  overflow: "hidden",
                                }}
                                data={[
                                  {
                                    label: "Type",
                                    // value: `${object?.gender ?? ""} ${object?.party_type_ID?.name ?? ""}`,
                                    value: (
                                      <span
                                        className="d-flex align-items-center justify-content-center"
                                        style={{ gap: "5px", whiteSpace: "nowrap" }}
                                      >
                                        <i className="ic ic-15 ic-parties" />
                                        {object?.gender ?? ""} {object?.party_type_ID?.name ?? ""}
                                      </span>
                                    ),
                                  },
                                  {
                                    label: "Liability",
                                    value: `${roundToInt(object?.liability_estimate)}% / ${roundToInt(object?.liability_percent)}% `,
                                  },
                                  {
                                    label: "Rep Letter",
                                    value: formatCalendarDate(object?.RepLetterSent),
                                  },
                                  {
                                    label: "Contact",
                                    value: formatCalendarDate(object?.contact_confirmed_date),
                                  },
                                  {
                                    label: "",
                                  },
                                ]}
                                onSelectReport={() => openEditForParty(object, "information")}
                                defaultLength={5}
                                hasBtn={true}
                                buttonData={[
                                  {
                                    id: "generate-document-button",
                                    buttonText: object?.party_served_date
                                      ? `Served: ${formatCalendarDate(object?.party_served_date)}`
                                      : "No Service Attempted",
                                    className: "p-l-6 p-r-5",
                                    style: { height: "25px" },
                                    onHover: () => {},
                                    onMouseLeave: () => {},
                                    onClick: () => {},
                                  },
                                ]}
                              />
                            </div>
                          </div>

                          <div
                            style={{
                              flex: 1,
                              minHeight: "160px",
                              paddingRight: "0px",
                              scrollbarWidth: "none",
                              minWidth: "min(400px, 100%)",
                              maxHeight: `${height}px`,
                            }}
                          >
                            <p className="menu-bar-style columnsTitle text-center">Notes</p>
                            <TreatmentNotesSection
                              entity_type={"OtherParty"}
                              record_id={object?.id}
                              module={"OtherParty"}
                              pagePanels={3}
                            />
                          </div>
                        </div>

                        <div className=" documents-wrapper">
                          <div className="col-12">
                            <DocumentRow
                              clientProvider={object}
                              page_id={9}
                              showSaveToPage={false}
                            />
                          </div>
                        </div>
                      </div>
                    </div>

                    {object.other_party_insurance?.map((data) => (
                      <div
                        className="report record-panel"
                        key={data.id}
                        style={{ position: "relative" }}
                      >
                        <PanelActionBarComponent
                          compact
                          instanceForName={data?.insurance_type?.name}
                          page_id={11}
                          id={data?.id}
                          name={`${object?.party_first_name || ""} ${object?.party_last_name}`}
                          type={data?.insurance_type?.name}
                          hasIcon={true}
                          iconClassName="insurance"
                        />
                        <div className="d-flex flex-wrap record-panel-body" style={{ gap: "5px" }}>
                          <div
                            className="leins-container-six m-b-5"
                            ref={insuranceHeightRef}
                            onClick={() => extractDataForEditModal(data, object)}
                          >
                            <div className="min-w-260px-client">
                              <ContactPanelForTreatment
                                panel_name={"Insurance Company"}
                                className="m-b-0"
                                dynamic_label={"Company Name"}
                                name={data?.company_contact?.name}
                                address1={data?.company_contact?.address1}
                                address2={data?.company_contact?.address2}
                                email={data?.company_contact?.email}
                                phone_number={data?.company_contact?.phone_number}
                                city={data?.company_contact?.city}
                                state={data?.company_contact?.state}
                                zip_code={data?.company_contact?.zip}
                                fax_number={data?.company_contact?.fax}
                                ext={data?.company_contact?.ext}
                                websiteURL={data?.company_contact?.website}
                                onSelectObject={() => handleEditModalShow("company")}
                                buttonData={InsuranceButtons}
                                pageName="OtherParty"
                              />
                            </div>
                            <div className="min-w-260px-client">
                              <ContactPanelForTreatment
                                panel_name={"Adjuster"}
                                className="m-b-0"
                                name={`${data?.adjuster?.first_name ?? ""} ${data?.adjuster?.last_name ?? ""}`}
                                address1={data?.adjuster?.address1}
                                address2={data?.adjuster?.address2}
                                email={data?.adjuster?.email}
                                phone_number={data?.adjuster?.phone_number}
                                fax_number={data?.adjuster?.fax}
                                city={data?.adjuster?.city}
                                state={data?.adjuster?.state}
                                zip_code={data?.adjuster?.zip}
                                ext={data?.adjuster?.ext}
                                websiteURL={data?.adjuster?.website}
                                onSelectObject={() => handleEditModalShow("adjuster")}
                                buttonData={InsuranceButtons}
                                pageName="Case Loans"
                              />
                            </div>
                            <div className="min-w-260px-client">
                              <ContactPanelForTreatment
                                panel_name={"Supervisor"}
                                className="m-b-0"
                                pageName="Case Loans"
                                name={`${data?.supervisor?.first_name ?? ""} ${data?.supervisor?.last_name ?? ""}`}
                                address1={data?.supervisor?.address1}
                                address2={data?.supervisor?.address2}
                                email={data?.supervisor?.email}
                                city={data?.supervisor?.city}
                                state={data?.supervisor?.state}
                                zip_code={data?.supervisor?.zip}
                                phone_number={data?.supervisor?.phone_number}
                                fax_number={data?.supervisor?.fax}
                                ext={data?.supervisor?.ext}
                                websiteURL={data?.supervisor?.website}
                                onSelectObject={() => handleEditModalShow("supervisor")}
                                buttonData={InsuranceButtons}
                              />
                            </div>
                            <div className="min-w-260px-client">
                              <InformationPanel
                                panel_name={"Claim Information"}
                                className="m-b-0"
                                labelClassName="font-weight-600"
                                labelStyle={{ color: "var(--primary-25)" }}
                                valueStyle={{
                                  maxWidth: "70%",
                                  display: "-webkit-box",
                                  WebkitBoxOrient: "vertical",
                                  WebkitLineClamp: 1,
                                  overflow: "hidden",
                                }}
                                data={[
                                  { label: "Type", value: data?.insurance_type?.name },
                                  {
                                    label: "Limits",
                                    value: `$ ${formatWithCommas(data?.liabilityLimit)} / ${formatWithCommas(data?.liabilityLimitAll)} $`,
                                  },
                                  { label: "Policy", value: data?.policy_number },
                                  { label: "Claim", value: data?.claim_number },
                                  {
                                    label: "Date",
                                    value: formatDateForPanelDisplay(data?.Dateconfirmedactive),
                                  },
                                ]}
                                onSelectReport={() => handleEditModalShow("claim")}
                                defaultLength={5}
                                hasBtn={true}
                                buttonData={EmploymentButtonsConfig2}
                              />
                            </div>

                            <div className="min-w-260px-client">
                              <ContactPanelForTreatment
                                name={`${data?.lien_adjuster?.contact?.first_name ?? ""} ${data?.lien_adjuster?.contact?.last_name ?? ""}`}
                                address1={data?.lien_adjuster?.contact?.address1}
                                address2={data?.lien_adjuster?.contact?.address2}
                                email={data?.lien_adjuster?.contact?.email}
                                phone_number={data?.lien_adjuster?.contact?.phone_number}
                                panel_name={"Lien Adjuster"}
                                buttonData={LienButtonsConfig}
                                dynamic_label={"Company Name"}
                                city={data?.lien_adjuster?.contact?.city}
                                state={data?.lien_adjuster?.contact?.state}
                                zip_code={data?.lien_adjuster?.contact?.zip}
                                fax_number={data?.lien_adjuster?.contact?.fax}
                                ext={data?.lien_adjuster?.contact?.ext}
                                websiteURL={data?.lien_adjuster?.contact?.website}
                                pageName="Case Loans"
                                className="m-b-0"
                                onSelectObject={() => handleEditModalShow("lien")}
                              />
                            </div>
                            <div className="min-w-260px-client">
                              <InformationPanel
                                onSelectReport={() => handleEditModalShow("lien_adjuster")}
                                panel_name={"Lien Information"}
                                className=""
                                labelClassName="font-weight-600"
                                labelStyle={{ color: "var(--primary-25)" }}
                                panelClassName="m-b-0"
                                data={[
                                  {
                                    label: "Lien Adjuster",
                                    value: data?.lien_adjuster?.name ?? "",
                                  },
                                  {
                                    label: "ERISA Protected Lien",
                                    value: (
                                      <Form.Check
                                        style={{
                                          width: "15px",
                                          height: "15px",
                                          accentColor: "grey",
                                        }}
                                        type="checkbox"
                                        className="protection-lien-cb"
                                        name="protection_lien"
                                        checked={data?.ERISAYN || false}
                                        readOnly
                                      />
                                    ),
                                  },
                                  {
                                    label: "Total Paid",
                                    value: data?.insurancelien_insurance?.totalpaid
                                      ? currencyFormat(data?.insurancelien_insurance?.totalpaid)
                                      : "$ 0.00",
                                  },
                                  {
                                    label: "Original Lien",
                                    value: data?.insurancelien_insurance?.liens
                                      ? currencyFormat(data?.insurancelien_insurance?.liens)
                                      : "$ 0.00",
                                  },
                                  {
                                    label: "Final Lien",
                                    value: data?.insurancelien_insurance?.lienfinal
                                      ? currencyFormat(data?.insurancelien_insurance?.lienfinal)
                                      : "$ 0.00",
                                  },
                                ]}
                                defaultLength={5}
                                hasBtn={true}
                                buttonData={EmploymentButtonsConfig2}
                              />
                            </div>
                          </div>
                          <div
                            style={{
                              flex: 1,
                              minHeight: "160px",
                              paddingRight: "0px",
                              scrollbarWidth: "none",
                              minWidth: "min(400px, 100%)",
                              maxHeight: `${height}px`,
                            }}
                          >
                            <p className="menu-bar-style columnsTitle text-center">Notes</p>
                            <TreatmentNotesSection
                              record_id={data.id}
                              instanceFor="OtherParty"
                              module={"Insurance"}
                              notesName={"Insurance"}
                              pagePanels={6}
                            />
                          </div>
                        </div>
                        <div className=" documents-wrapper">
                          <div className="col-12">
                            {/* <div className="height-25">
                              <h4 className="text-capitalize d-flex align-items-center justify-content-center h-100 text-lg text-upper-case background-main-10 font-weight-semibold text-center client-contact-title">
                                &nbsp;Document Row
                              </h4>
                            </div> */}
                            <DocumentRow
                              showSaveToPage={false}
                              clientProvider={data}
                              page_id={11}
                            />
                          </div>
                        </div>
                      </div>
                    ))}

                    {object?.other_party_counsel?.map((data, index) => (
                      <CounselMain
                        key={index}
                        opposingcounsel={data}
                        entity_name={"OtherParty"}
                        // For_instance_name={""}
                        For_instance_name={`${object?.party_first_name || ""} ${object?.party_last_name}`}
                        handleFacthDefendants={refetch}
                        setForDeleteCounselId={setForDeleteCounselId}
                        showDeleteCounselConfirm={setShowDeleteCounselConfirm}
                        isDef={true}
                        isParty={true}
                      />
                    ))}
                  </div>
                ))
              ))}

            <NotesSectionDashboard pageName={"Parties"} />
          </div>
        </div>
      </div>
      <Footer />

      {show && (
        <AddOtherPartiesModal
          show={show}
          handleClose={handleCloseAddModal}
          refetch={() => {
            fetchDummyStatus();
            refetch();
          }}
          isDummy={isDummy}
        />
      )}

      {showDocument && (
        <GenrateDocument
          show={true}
          handleClose={() => setShowDocument(false)}
          PageEntity={PageEntity}
          dropdownName={dropdownName}
          instanceId={instanceIdForGenrateDoc}
        />
      )}

      {showDeleteCounselConfirm && (
        <ConfirmDeletePopup
          entity="Counsel"
          handleClose={() => setShowDeleteCounselConfirm(false)}
          handleDeleteSubmission={handleDeleteCounselSubmission}
        />
      )}

      {insuranceModalShow && (
        <InsuranceModal
          states={modalData?.states}
          insuranceTypes={modalData?.insurance_types}
          insurance={editInsuranceModal}
          initialParty={selectedInsuranceParty}
          litigation={modalData?.litigation}
          show={insuranceModalShow}
          handleInsuranceCreation={() => {
            refetch();
            fetchMedicalBillData();
          }}
          handleClose={() => setInsuranceModalShow(false)}
          activeTab={activeInsuranceTab}
          deleteInsuranceHandler={deleteInsuranceHandler}
          InsuranceButtons={InsuranceButtons}
          billData={medicalBillData}
        />
      )}

      {showDeleteInsuranceModal && (
        <ConfirmDeletePopup
          entity="Insurance"
          handleClose={() => setShowDeleteInsuranceModal(false)}
          handleDeleteSubmission={handleDelete}
        />
      )}

      {showDeleteConfirm && (
        <ConfirmDeletePopup
          entity="Party"
          handleClose={hideDeleteConfirmPopup}
          handleDeleteSubmission={handleDeletePartySubmission}
        />
      )}

      {showEditPartyModal && (
        <AddOtherPartiesModal
          show={showEditPartyModal}
          handleClose={handleCloseFunction}
          refetch={refetch}
          isDummy={isDummy}
          isEdit={true}
          activeTab={selectedEditableTabPanel.current}
          setReducer={setReducer}
          reducerValue={reducerValue}
          defendant={editPartyData}
          deleteConfirmPopup={showDeleteConfirmPopup}
        />
      )}

      {editDefendantPartyType && (
        <AddDefendantModal
          show={editDefendantPartyType}
          handleClose={() => setEditDefendantPartyType(false)}
          refetchDef={refetch}
          isDummy={false}
          isEdit={true}
          activeTab={selectedEditableTabPanel.current}
          setReducer={setReducer}
          reducerValue={reducerValue}
          defendant={selectedData}
          deleteConfirmPopup={showDeleteConfirmPopup}
        />
      )}

      {editClientPartyType && (
        <ClientEditModal
          show={editClientPartyType}
          handleClose={() => {
            addClickRecord(999, 2);
            setEditClientPartyType(false);
          }}
          clientData={{ client: selectedData }}
          refetch={refetch}
          notClientPage
        />
      )}
      {/* <ChangePartyTypeModal
                show={editClientPartyType}
                handleClose={() => setEditClientPartyType(false)}
                party={selectedData}
                refetch={refetch}
                isDefendant={false}
                isClient={true}
              /> */}
    </>
  );
}

export default OtherPartiesPage;
